import React, { useState } from "react";

import {
  DefaultButton,
  FilterBar,
  Item,
  Stack,
  useTheme
} from "@bps/fluent-ui";
import { SectionTitle } from "@components/SectionTitle";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  Tenant,
  TenantUserSortField
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { NewUserDialogButton } from "./components/NewUserDialogButton";
import { PltUsersTable } from "./components/PltUsersTable";
import { SendNotificationsDialog } from "./components/SendNotificationsDialog";

interface TenantUsersProps {
  tenant: Tenant;
}

type BooleanString = "true" | "false";
type PremStatus = "active" | "inactive";

export interface TenantUsersFilters {
  searchTerm: string;
  bpIdUserId?: string;
  hasBpId?: BooleanString;
  hasExternalId?: BooleanString;
  isInactive?: BooleanString;
  userStatusCode?: PremStatus;
  sortField?: TenantUserSortField;
  sortAscending?: boolean;
}

const defaultFilter: TenantUsersFilters = {
  searchTerm: "",
  bpIdUserId: undefined,
  hasBpId: undefined,
  hasExternalId: undefined,
  isInactive: "false",
  userStatusCode: "active",
  sortField: undefined,
  sortAscending: undefined
};

export const TenantUsersScreen: React.FC<TenantUsersProps> = ({ tenant }) => {
  const theme = useTheme();
  const { data: hasNotificationPermission } = usePermissionsCheckQuery(
    Permissions.PltNotificationsWrite
  );

  const [selectedUsersIds, setSelectedUsersIds] = useState<Set<string>>(
    new Set<string>()
  );

  const [showDialog, setShowDialog] = useState<boolean>(false);

  const items: Item<TenantUsersFilters>[] = [
    {
      name: "searchTerm",
      type: "searchBox",
      stickItem: true,
      props: {
        styles: { root: { minWidth: 220 } },
        placeholder: "Search by name or email",
        removeSpecialCharacters: false
      }
    },
    {
      name: "bpIdUserId",
      type: "searchBox",
      props: {
        styles: { root: { minWidth: 200 } },
        placeholder: "Search by Bp User Id",
        removeSpecialCharacters: false
      }
    },
    {
      name: "hasBpId",
      type: "optionsSelect",
      props: {
        multiSelect: false,
        placeholder: "Have Bp ID?",
        hideSearchOption: true,
        styles: { root: { width: 160 } },
        options: [
          { key: "true", text: "Have Bp ID" },
          { key: "false", text: "Don't have Bp ID" }
        ]
      }
    },
    {
      name: "hasExternalId",
      type: "optionsSelect",
      props: {
        multiSelect: false,
        placeholder: "Have External Id?",
        hideSearchOption: true,
        styles: { root: { width: 200 } },
        options: [
          { key: "true", text: "Have External ID" },
          { key: "false", text: "Don't have External ID" }
        ]
      }
    },
    {
      name: "isInactive",
      type: "optionsSelect",
      props: {
        styles: { root: { width: 130 } },
        multiSelect: false,
        placeholder: "User status",
        hideSearchOption: true,
        options: [
          { key: "false", text: "Active users" },
          { key: "true", text: "Inactive users" }
        ]
      }
    },
    {
      name: "userStatusCode",
      type: "optionsSelect",
      props: {
        styles: { root: { width: 167 } },
        multiSelect: false,
        placeholder: "User Prem status",
        hideSearchOption: true,
        options: [
          { key: "active", text: "Active Prem users" },
          { key: "inactive", text: "Inactive Prem users" }
        ]
      }
    }
  ];

  const onDismissNotificationsDialog = () => {
    setSelectedUsersIds(new Set());
    setShowDialog(false);
  };

  return (
    <Stack
      verticalFill
      styles={{ root: { padding: theme.spacing.s1 } }}
      tokens={{ childrenGap: theme.spacing.l2 }}
    >
      <Stack horizontal horizontalAlign="space-between">
        <SectionTitle>Users</SectionTitle>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <NewUserDialogButton tenantId={tenant.id} />
          <DefaultButton
            disabled={!selectedUsersIds.size || !hasNotificationPermission}
            onClick={() => setShowDialog(true)}
            title={
              !hasNotificationPermission ? NO_ACTION_PERMISSION : undefined
            }
          >
            Send Batch Notifications
          </DefaultButton>
        </Stack>
      </Stack>
      <FilterBar<TenantUsersFilters>
        items={items}
        initialValues={defaultFilter}
      >
        {({ values }) => {
          return (
            <PltUsersTable
              tenantId={tenant.id}
              filter={values}
              selectedUsersIds={selectedUsersIds}
              setSelectedUsersIds={setSelectedUsersIds}
              showNotificationsDialog={() => setShowDialog(true)}
            />
          );
        }}
      </FilterBar>

      {showDialog && (
        <SendNotificationsDialog
          tenantId={tenant.id}
          usersIds={Array.from(selectedUsersIds.values())}
          onDismiss={onDismissNotificationsDialog}
        />
      )}
    </Stack>
  );
};
